import { publicClient } from './apiClients'
import { BookingV2 } from '@customTypes/Booking'
import { getAppointmentFromBookingData } from './mappers'
import { getRecaptchaSiteKey } from 'src/helpers/getRecaptchaSiteKey'
import { getQueryParams } from 'src/utils/getQueryParams'
import { captureException } from '@sentry/browser'
import { reportErrorToSentry } from 'src/utils/reportErrorToSentry'
import { redactSensitiveKeys } from '@dentalux/ui-library-core/utils'
import { get } from 'lodash'

export interface VerifyPhoneResponse {
  status: string // e.g. 'SMS_CODE_GENERATED'
  validity: string // e.g. '2023-10-12T11:00:09.789+02:00'
  phone?: string
}

const recaptchaAction = 'verify_new_patient_phone'

export const verifyPhoneNumberForNewPatient = async (bookingData: BookingV2): Promise<VerifyPhoneResponse> => {
  return new Promise((resolve, reject) => {
    if (!grecaptcha) {
      reportErrorToSentry('grecaptcha is not defined when verifying phone number')
      reject()
    }

    try {
      grecaptcha.ready(function () {
        grecaptcha.execute(getRecaptchaSiteKey(), { action: recaptchaAction }).then(async function (token) {
          const appointment = getAppointmentFromBookingData(bookingData)
          const domain = window.location.hostname.replace('www.', '')
          const queryParamToken = getQueryParams()?.recaptchaTestToken
          const recaptchaToken = queryParamToken || token

          try {
            const response = await publicClient.post<VerifyPhoneResponse>(
              '/appointment-bundles/verify',
              { ...appointment },
              {
                params: { domain, lang: bookingData.lang },
                headers: {
                  'g-recaptcha-response': recaptchaToken,
                  'g-action': recaptchaAction,
                },
              }
            )

            resolve({ ...response.data, phone: appointment.phoneNumber })
          } catch (error) {
            const response = redactSensitiveKeys(get(error, 'response.data'))
            captureException(error, {
              extra: {
                message: 'Fail inside verifyPhoneNumberForNewPatient Promise',
                response,
                payload: redactSensitiveKeys(appointment),
              },
            })
            reject(error)
          }
        })
      })
    } catch (error) {
      captureException(error, {
        extra: { message: 'Fail inside verifyPhoneNumberForNewPatient try/catch' },
      })
    }
  })
}

export const requestReturningPatientOtpCode = async (payload: {
  phone: string
  clinicId: string
  lang: string
}): Promise<VerifyPhoneResponse> => {
  const response = await publicClient.post<VerifyPhoneResponse>(
    '/phone/otp',
    {
      clinicReferenceId: payload.clinicId,
      phoneNumber: payload.phone,
    },
    {
      params: { lang: payload.lang },
    }
  )

  return { ...response.data, phone: payload.phone }
}

export interface VerifyPhoneNumberOldPatientPayload {
  mobilePhoneNumber: string
  otpCode: string
  treatmentBundleReferenceId: string
  clinicReferenceId: string
}

export interface ReturningPatient {
  fullName: string
  patientReferenceId: string
  practitionerReferenceId?: string
}

export const verifyPhoneNumberForOldPatient = async (data: VerifyPhoneNumberOldPatientPayload) => {
  const response = await publicClient.post<ReturningPatient[]>('/phone/otp/validate-code', {
    mobilePhoneNumber: data.mobilePhoneNumber,
    otpCode: data.otpCode,
    treatmentBundleReferenceId: data.treatmentBundleReferenceId,
    clinicReferenceId: data.clinicReferenceId,
  })

  return response.data
}
